import { useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { airSavedCardStore, GetAirCardsFunc } from "entities/AirSavedCard";
import { AirQueryParams, AirSearchRequestFunc, createAirSearchQueryParams } from "entities/AirSearchCard";
import { airSearchCardStore } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { RequiredBy } from "shared/lib/typeCheck.lib";

import { getInitialSearchQPM } from "../ParamsManager";
import { getAirReferenceFromUrl } from "../urlQueryParam.utils";

type UseAirUrlQueryBasedInitialSearchProps = {
  searchController: {
    searchRequest: AirSearchRequestFunc;
    getCards: GetAirCardsFunc;
    isSearchLoading: boolean;
  };
  openedCardAction: (card: ShipmentModel | null) => { type: string; payload: ShipmentModel | null };
};

type UseAirUrlQueryBasedInitialSearchReturnType = {
  airInitialSearch: ({ requestedFromUrl }: { requestedFromUrl: boolean }) => void;
};

export const useAirUrlQueryBasedInitialSearch = ({
  searchController,
  openedCardAction,
}: UseAirUrlQueryBasedInitialSearchProps): UseAirUrlQueryBasedInitialSearchReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const searchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const { showOpenedCardView, showMainView, showIntroView } = usePanelViews();
  const { searchRequest, getCards, isSearchLoading } = searchController;
  const initialSearchQPM = useRef(getInitialSearchQPM()).current;

  const createRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    requestParams: RequiredBy<AirQueryParams, "awb_number">
  ): (() => void) | null => {
    const reqParams = createAirSearchQueryParams({
      requestType: "search",
      override: requestParams,
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          getCards(
            { where: { reference: requestParams.awb_number } },
            {
              onSettled: (userSearchResponse) => {
                searchRequest(reqParams, {
                  onSuccess: (searchResponse) => {
                    if (searchResponse.requestLimitReached) return;
                    dispatch(openedCardAction(searchResponse));
                  },
                  onSettled: (searchResponse) => {
                    if (userSearchResponse?.length) {
                      dispatch(airSavedCardStore.actions.prependSavedCardAction({ data: userSearchResponse[0] }));
                      return;
                    }

                    if (searchResponse && !searchResponse?.requestLimitReached) {
                      dispatch(airSearchCardStore.actions.prependSearchCardAction({ data: searchResponse }));
                    }
                  },
                });
              },
            }
          );
        };
      case "not-authenticated":
      default:
        return () => {
          searchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (searchResponse) => {
              dispatch(airSearchCardStore.actions.prependSearchCardAction({ data: searchResponse }));
              dispatch(openedCardAction(searchResponse));
            },
          });
        };
    }
  };

  const airInitialSearch = ({ requestedFromUrl }: { requestedFromUrl: boolean }) => {
    if (!requestedFromUrl) return;
    const airReferenceQueryValue = getAirReferenceFromUrl(initialSearchQPM);
    if (!airReferenceQueryValue) return;

    dispatch(appStore.actions.setRequestedFromUrl(true));
    showOpenedCardView();

    const requestFunc = createRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", {
      awb_number: airReferenceQueryValue,
      route_path: true,
    });

    requestFunc?.();
  };

  return { airInitialSearch };
};
